import { FC } from "react";

import { Box } from "@mui/material";
import { FormTitleFontStyle } from "@simplyk/common";
import { Control, FieldPath, FieldValues, useController } from "react-hook-form";

import { getFontStyleConfig } from "@/canvas/hooks/useFontStyleConfig";
import { useMediaQuery } from "@/common/hooks/useMediaQuery";
import { Translate, useTranslate } from "@/common/hooks/useTranslate";
import { LiveEditorBlock } from "@/components/LiveEditorElements/LiveEditorBlock/LiveEditorBlock";
import { DashboardTypography } from "@/dashboard-design-system/Typography/DashboardTypography";

const FONT_STYLE_OPTIONS = [
  FormTitleFontStyle.Classic,
  FormTitleFontStyle.Bubbly,
  FormTitleFontStyle.Elegant,
  FormTitleFontStyle.SlabSerif,
  FormTitleFontStyle.Futuristic,
  FormTitleFontStyle.Handwritten,
] as const;

const getFontStyleLabel = (t: Translate, fontStyle: FormTitleFontStyle): string => {
  switch (fontStyle) {
    case FormTitleFontStyle.Classic:
      return t("dashboard", "liveFormEditor.stepTitle.fontStyle.classic");
    case FormTitleFontStyle.Elegant:
      return t("dashboard", "liveFormEditor.stepTitle.fontStyle.elegant");
    case FormTitleFontStyle.Handwritten:
      return t("dashboard", "liveFormEditor.stepTitle.fontStyle.handwritten");
    case FormTitleFontStyle.SlabSerif:
      return t("dashboard", "liveFormEditor.stepTitle.fontStyle.slabSerif");
    case FormTitleFontStyle.Futuristic:
      return t("dashboard", "liveFormEditor.stepTitle.fontStyle.futuristic");
    case FormTitleFontStyle.Bubbly:
      return t("dashboard", "liveFormEditor.stepTitle.fontStyle.bubbly");
  }
};

interface FontStyleSelectorProps<TFieldValues extends FieldValues> {
  name: FieldPath<TFieldValues>;
  control: Control<TFieldValues>;
  onFontStyleChange?: (fontStyle: FormTitleFontStyle) => void;
}

export const FontStyleSelector = <TFieldValues extends FieldValues>({
  name,
  control,
  onFontStyleChange,
}: FontStyleSelectorProps<TFieldValues>): ReturnType<FC> => {
  const { t } = useTranslate();
  const { field } = useController({ name, control });
  const selectedValue = field.value ?? FormTitleFontStyle.Classic;

  const handleSelect = (fontStyle: FormTitleFontStyle) => {
    field.onChange(fontStyle);
    onFontStyleChange?.(fontStyle);
  };

  return (
    <LiveEditorBlock gap={1.5}>
      <DashboardTypography variant="subtitle1">
        {t("dashboard", "liveFormEditor.stepTitle.fontStyle.label")}
      </DashboardTypography>
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "repeat(3, 1fr)",
          gap: 1,
        }}
      >
        {FONT_STYLE_OPTIONS.map((fontStyle) => (
          <FontStyleOptionButton
            key={fontStyle}
            fontStyle={fontStyle}
            isSelected={selectedValue === fontStyle}
            onSelect={() => handleSelect(fontStyle)}
          />
        ))}
      </Box>
    </LiveEditorBlock>
  );
};

interface FontStyleOptionButtonProps {
  fontStyle: FormTitleFontStyle;
  isSelected: boolean;
  onSelect: () => void;
}

const FontStyleOptionButton: FC<FontStyleOptionButtonProps> = ({ fontStyle, isSelected, onSelect }) => {
  const { t } = useTranslate();
  const { isSmallScreen } = useMediaQuery();
  const config = getFontStyleConfig(fontStyle);

  return (
    <Box
      onClick={onSelect}
      data-test={`font-style-option-${fontStyle}`}
      sx={(theme) => ({
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        paddingY: 2,
        borderRadius: theme.radius(1),
        cursor: "pointer",
        border: `1.5px solid ${isSelected ? theme.palette.border.brand.intense : theme.palette.border.neutral.quiet}`,
        transition: "border-color 0.15s ease-in",
        "&:hover": {
          borderColor: isSelected ? theme.palette.border.brand.intense : theme.palette.border.neutral.moderate,
        },
      })}
    >
      <DashboardTypography
        variant={isSmallScreen ? "caption" : "body2"}
        sx={(theme) => ({
          fontFamily: config.fontFamily,
          fontWeight: config.fontWeight,
          color: isSelected ? theme.palette.text.brand.moderate : theme.palette.text.neutral.quiet,
          transition: "color 0.1s ease-in",
        })}
      >
        {getFontStyleLabel(t, fontStyle)}
      </DashboardTypography>
    </Box>
  );
};
